/**
 * @file Input atom — data-driven text field (RFC §3).
 * @description Narrows the Gluestack compound Input: callers pass data
 * (value, placeholder, icons), never InputField/InputSlot trees.
 * @module components/atoms/Input
 */
import React from "react";

import {
  Input as UIInput,
  InputField,
  InputIcon,
  InputSlot,
} from "@/components/ui/input";

import { withTextVariant } from "../types";

import type { IconSize } from "../Icon";
import type { TextVariant, WithUnsafeStyle } from "../types";

/**
 *
 */
type UIInputProps = React.ComponentProps<typeof UIInput>;
/**
 *
 */
type UIInputFieldProps = React.ComponentProps<typeof InputField>;
/** Imperative focus handle of the inner text field. */
export type InputFieldRef = React.ComponentRef<typeof InputField> & {
  /** The forwarded native/web text field supports imperative focus. */
  focus: () => void;
};
/**
 *
 */
type IconComponent = React.ElementType;

/**
 *
 */
export type InputVariant = "outline" | "underlined" | "rounded";
/**
 *
 */
export type InputSize = "sm" | "md" | "lg" | "xl";

/**
 * Closed semantic color tier for the inner icon slots. Maps to a fixed
 * content-token class table — no raw hex, no free className. The sanctioned,
 * closed-enum way to recolor a leading/trailing icon.
 */
export type InputIconTone = "primary" | "muted" | "subtle";

/**
 * Closed icon-tone → closed content-token class. Every value is a design-system
 * semantic token (content tier). The closure guard is the exhaustive per-tone
 * unit test in `__tests__` — it fails if any entry drifts to raw palette, an
 * arbitrary value, or a typo'd token.
 */
const ICON_TONE_TO_CLASS: Record<InputIconTone, string> = {
  primary: "text-content-primary",
  muted: "text-content-muted",
  subtle: "text-content-subtle",
};

/**
 *
 */
export interface InputProps
  extends
    Omit<UIInputProps, "variant" | "size" | "style" | "children">,
    WithUnsafeStyle<UIInputProps["style"]> {
  variant?: InputVariant;
  size?: InputSize;
  /** Optional leading icon (Lucide). */
  leadingIcon?: IconComponent;
  /** Optional trailing icon (Lucide), pressable via onTrailingIconPress. */
  trailingIcon?: IconComponent;
  /** Press handler for the trailing icon slot (e.g. clear / reveal). */
  onTrailingIconPress?: () => void;
  /** Test identity for the pressable trailing icon slot (SE-6948). */
  trailingIconTestID?: string;
  /** Accessibility label for the pressable trailing icon slot (SE-6948). */
  trailingIconAccessibilityLabel?: string;
  /** Closed icon-size enum (reuses the Icon atom's `IconSize`) for the leading icon. */
  leadingIconSize?: IconSize;
  /** Closed icon-size enum (reuses the Icon atom's `IconSize`) for the trailing icon. */
  trailingIconSize?: IconSize;
  /** Closed semantic tone for the leading icon (content-token table). */
  leadingIconTone?: InputIconTone;
  /** Closed semantic tone for the trailing icon (content-token table). */
  trailingIconTone?: InputIconTone;
  /**
   * Closed text-style enum for the inner field, sharing the `Text`/`Heading`
   * vocabulary (`TextVariant`). This is the sanctioned way to set the field's
   * typography — it replaces reaching for raw size/weight utilities through
   * `field.className`.
   *
   * Omit it to inherit today's rendering exactly: the vendored field style
   * contributes a size for the atom's `size` prop and no family at all, so an
   * omitted variant leaves the field's className byte-identical. Setting a
   * variant is what opts a field into the bundled Satoshi ladder.
   */
  textVariant?: TextVariant;
  /** Props forwarded to the inner text field (value, onChangeText, …). */
  field: Omit<UIInputFieldProps, "children">;
}

const Input = React.forwardRef<React.ComponentRef<typeof UIInput>, InputProps>(
  function Input(
    {
      variant = "outline",
      size = "md",
      leadingIcon,
      trailingIcon,
      onTrailingIconPress,
      trailingIconTestID,
      trailingIconAccessibilityLabel,
      leadingIconSize,
      trailingIconSize,
      leadingIconTone,
      trailingIconTone,
      textVariant,
      field,
      UNSAFE_style,
      ...props
    },
    ref
  ) {
    return (
      <UIInput
        ref={ref}
        variant={variant}
        size={size}
        style={UNSAFE_style}
        {...props}
      >
        {leadingIcon ? (
          <InputSlot className="pl-3">
            <InputIcon
              as={leadingIcon}
              size={leadingIconSize}
              className={
                leadingIconTone
                  ? ICON_TONE_TO_CLASS[leadingIconTone]
                  : undefined
              }
            />
          </InputSlot>
        ) : null}
        {/*
          No variant → no className override is emitted at all, so the props
          reaching the field are the pre-SE-6596 props object, not an equal
          one. The no-op default is structural rather than incidental.
        */}
        <InputField
          {...field}
          {...(textVariant === undefined
            ? {}
            : { className: withTextVariant(textVariant, field.className) })}
        />
        {trailingIcon ? (
          <InputSlot
            className="pr-3"
            onPress={onTrailingIconPress}
            testID={trailingIconTestID}
            accessibilityLabel={trailingIconAccessibilityLabel}
          >
            <InputIcon
              as={trailingIcon}
              size={trailingIconSize}
              className={
                trailingIconTone
                  ? ICON_TONE_TO_CLASS[trailingIconTone]
                  : undefined
              }
            />
          </InputSlot>
        ) : null}
      </UIInput>
    );
  }
);

Input.displayName = "Input";

export { Input };
